/**
 * LandingPage
 * Public homepage for ClinHx - Anglesea Sports Medicine
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Activity, FileText, Users, Clock, CheckCircle, 
  ArrowRight, Shield, Calendar, Clipboard
} from 'lucide-react';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={32} color="#2563eb" />
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' }}>
                ClinHx
              </h1>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                Anglesea Sports Medicine
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => navigate('/referrer/login')}
              style={{
                padding: '10px 20px',
                backgroundColor: 'white',
                color: '#e8941c',
                border: '2px solid #e8941c',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#e8941c';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = '#e8941c';
              }}
            >
              Referrer Portal
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1d4ed8'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563eb'}
            >
              Staff Login
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #0ea5e9 100%)',
        padding: '80px 24px',
        textAlign: 'center',
        color: 'white',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 42,
            fontWeight: 800,
            margin: '0 0 20px',
            lineHeight: 1.2,
          }}>
            Streamlined Patient History & Referral Management
          </h2>
          <p style={{
            fontSize: 20,
            opacity: 0.9,
            margin: '0 0 40px',
            lineHeight: 1.6,
          }}>
            ClinHx simplifies patient intake for sports medicine consultations. 
            Patients complete comprehensive history forms before their appointment, 
            enabling more efficient and focused clinical encounters.
          </p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/referrer/login')}
              style={{
                padding: '14px 28px',
                backgroundColor: '#e8941c',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 14px rgba(232,148,28,0.4)',
              }}
            >
              Referrer Portal <ArrowRight size={18} />
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '14px 28px',
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              Staff Login <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '80px 24px', backgroundColor: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h3 style={{
            textAlign: 'center',
            fontSize: 32,
            fontWeight: 700,
            color: '#1e293b',
            margin: '0 0 16px',
          }}>
            How ClinHx Works
          </h3>
          <p style={{
            textAlign: 'center',
            fontSize: 18,
            color: '#64748b',
            margin: '0 0 60px',
            maxWidth: 600,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            A seamless workflow from referral to consultation
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 30,
          }}>
            {/* Feature 1 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <FileText size={28} color="#2563eb" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                Referral Upload
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Upload referral documents or use the referrer portal to submit patient details directly. 
                AI extracts key information automatically.
              </p>
            </div>

            {/* Feature 2 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <Clipboard size={28} color="#d97706" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                Patient History Forms
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Patients receive a link to complete comprehensive, condition-specific history forms 
                before their appointment.
              </p>
            </div>

            {/* Feature 3 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <Calendar size={28} color="#16a34a" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                Appointment Booking
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Patients select their preferred appointment time. Bookings are confirmed 
                and synced with practice management systems.
              </p>
            </div>

            {/* Feature 4 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#f3e8ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <CheckCircle size={28} color="#9333ea" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                AI-Generated Letters
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Consultation letters and radiology referrals are automatically generated 
                based on patient history and clinical findings.
              </p>
            </div>

            {/* Feature 5 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <Shield size={28} color="#dc2626" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                ACC Integration
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Streamlined ACC claim handling with impairment assessment support 
                and automated form population.
              </p>
            </div>

            {/* Feature 6 */}
            <div style={{
              padding: 30,
              backgroundColor: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                backgroundColor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}>
                <Clock size={28} color="#0284c7" />
              </div>
              <h4 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
                Time Savings
              </h4>
              <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6 }}>
                Reduce administrative burden with automated workflows, 
                allowing more time for patient care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Referrer CTA Section */}
      <section style={{
        padding: '60px 24px',
        backgroundColor: '#fef7ed',
        borderTop: '1px solid #fed7aa',
        borderBottom: '1px solid #fed7aa',
      }}>
        <div style={{
          maxWidth: 900,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 30,
        }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <h3 style={{
              fontSize: 28,
              fontWeight: 700,
              color: '#92400e',
              margin: '0 0 12px',
            }}>
              For Referring Practitioners
            </h3>
            <p style={{
              fontSize: 16,
              color: '#b45309',
              margin: 0,
              lineHeight: 1.6,
            }}>
              Register for the Referrer Portal to submit patient referrals directly, 
              track referral status, and receive consultation reports electronically.
            </p>
          </div>
          <button
            onClick={() => navigate('/referrer/login')}
            style={{
              padding: '14px 32px',
              backgroundColor: '#e8941c',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 14px rgba(232,148,28,0.3)',
              whiteSpace: 'nowrap',
            }}
          >
            <Users size={18} /> Access Referrer Portal
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#1e293b',
        color: 'white',
        padding: '40px 24px',
      }}>
        <div style={{
          maxWidth: 1100,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Activity size={24} color="#3b82f6" />
              <span style={{ fontSize: 18, fontWeight: 700 }}>ClinHx</span>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: '#94a3b8' }}>
              Anglesea Sports Medicine<br />
              Hamilton, New Zealand
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: 24 }}>
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Staff Login
            </button>
            <button
              onClick={() => navigate('/referrer/login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Referrer Portal
            </button>
          </div>
        </div>
        
        <div style={{
          maxWidth: 1100,
          margin: '30px auto 0',
          paddingTop: 20,
          borderTop: '1px solid #334155',
          textAlign: 'center',
          fontSize: 13,
          color: '#64748b',
        }}>
          © {new Date().getFullYear()} Anglesea Sports Medicine. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
